import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/UI/ToastProvider';
import { Button } from '../../components/UI/Button';
import { ConfirmModal } from '../../components/UI/ConfirmModal';
import {
  userPrivateDocumentService,
  UserPrivateDocumentDto,
  MyPrivateStorageUsageDto,
} from '../../services/userPrivateDocumentService';
import { documentService, DocumentPickerNode } from '../../services/documentService';
import { formatBytes } from '../../utils/formatBytes';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

function indexStatusLabel(doc: UserPrivateDocumentDto): string {
  if (doc.indexedForChatbot) return 'Indexado';
  const s = (doc.processingStatus || '').toLowerCase();
  if (s.includes('fail') || s.includes('erro')) return 'Falha na indexação';
  if (s.includes('pend') || s === '' || s === 'uploaded') return 'Pendente / em processamento';
  return doc.processingStatus || '—';
}

const MyPrivateDocumentsPage: React.FC = () => {
  const { addToast } = useToast();
  const [docs, setDocs] = useState<UserPrivateDocumentDto[]>([]);
  const [usage, setUsage] = useState<MyPrivateStorageUsageDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishDoc, setPublishDoc] = useState<UserPrivateDocumentDto | null>(null);
  const [folders, setFolders] = useState<DocumentPickerNode[]>([]);
  const [foldersLoading, setFoldersLoading] = useState(false);
  const [folderSearch, setFolderSearch] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [publishNotes, setPublishNotes] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UserPrivateDocumentDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [list, u] = await Promise.all([
        userPrivateDocumentService.list(),
        userPrivateDocumentService.myUsage(),
      ]);
      setDocs(list);
      setUsage(u);
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao carregar seus documentos.'), 'error');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (!publishDoc) return;
    let cancelled = false;
    setFoldersLoading(true);
    documentService
      .getFolderPickerNodes()
      .then(nodes => {
        if (!cancelled) setFolders(nodes);
      })
      .catch(() => {
        if (!cancelled) {
          addToast('Não foi possível carregar pastas para sugestão de destino.', 'error');
          setFolders([]);
        }
      })
      .finally(() => {
        if (!cancelled) setFoldersLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [publishDoc, addToast]);

  const filteredFolders = useMemo(() => {
    const q = folderSearch.trim().toLowerCase();
    if (q.length < 2) return [];
    return folders
      .filter(
        f =>
          f.title.toLowerCase().includes(q) ||
          f.fullPath.toLowerCase().includes(q) ||
          f.path.toLowerCase().includes(q)
      )
      .slice(0, 100);
  }, [folders, folderSearch]);

  const handleDownload = async (doc: UserPrivateDocumentDto) => {
    try {
      const { url } = await userPrivateDocumentService.getDownloadUrl(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao obter link de download.'), 'error');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await userPrivateDocumentService.delete(deleteTarget.id);
      addToast('Documento removido.', 'success');
      setDeleteTarget(null);
      await loadAll();
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Erro ao excluir documento.'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const submitPublish = async () => {
    if (!publishDoc) return;
    setPublishing(true);
    try {
      await userPrivateDocumentService.createPublishRequest(publishDoc.id, {
        targetDocumentId: selectedFolderId || undefined,
        notes: publishNotes.trim() || undefined,
      });
      addToast('Solicitação de publicação enviada.', 'success');
      setPublishDoc(null);
      setSelectedFolderId(null);
      setPublishNotes('');
      setFolderSearch('');
    } catch (e: unknown) {
      addToast(apiErrorMessage(e, 'Não foi possível registrar a solicitação.'), 'error');
    } finally {
      setPublishing(false);
    }
  };

  const usagePercent = usage
    ? Math.min(100, Math.max(0, Number(usage.percentUsed) || 0))
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Privados</h1>
          <p className="text-sm text-gray-600 mt-1">
            Arquivos privados usados nas respostas do chat após a indexação.{' '}
            <Link to="/copilot" className="text-blue-600 hover:underline">
              Voltar ao Copiloto
            </Link>
          </p>
        </div>
        <Button type="button" variant="secondary" onClick={() => void loadAll()} disabled={loading}>
          Atualizar
        </Button>
      </div>

      {usage && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <span className="text-sm font-medium text-gray-800">Uso do seu drive privado</span>
            {!usage.quotaConfigured ? (
              <span className="text-sm text-amber-700">Quota do tenant ainda não configurada — uploads podem estar bloqueados.</span>
            ) : (
              <span className="text-sm text-gray-600">
                {formatBytes(usage.totalSizeBytes)} de {formatBytes(usage.maxStoragePerUserBytes)} (
                {usagePercent.toFixed(1)}%)
              </span>
            )}
          </div>
          <div className="h-3 w-full rounded-full bg-gray-200 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${usagePercent > 90 ? 'bg-red-500' : 'bg-blue-600'}`}
              style={{ width: `${usagePercent}%` }}
            />
          </div>
          {usage.globalMaxStorageBytes != null && (
            <p className="text-xs text-gray-500 mt-2">
              Uso total do tenant (todos os usuários): {formatBytes(usage.totalTenantUsedBytes)}
            </p>
          )}
        </div>
      )}

      {loading ? (
        <p className="text-gray-500">Carregando...</p>
      ) : docs.length === 0 ? (
        <p className="text-gray-500">Nenhum arquivo no seu drive privado. Envie um pelo ícone de anexo no chat.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Nome</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Tipo</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Tamanho</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Indexação</th>
                <th className="text-right py-3 px-4 font-medium text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {docs.map(doc => (
                <tr key={doc.id} className="border-t border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-900">{doc.fileName}</td>
                  <td className="py-3 px-4 text-gray-600">{doc.fileType || '—'}</td>
                  <td className="py-3 px-4 text-gray-600">{formatBytes(doc.fileSize)}</td>
                  <td className="py-3 px-4 text-gray-600">{indexStatusLabel(doc)}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => void handleDownload(doc)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                      </Button>
                      <Button type="button" variant="secondary" size="sm" onClick={() => setPublishDoc(doc)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </Button>
                      <Button type="button" variant="danger" size="sm" onClick={() => setDeleteTarget(doc)}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {publishDoc && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Solicitar publicação</h3>
            <p className="text-sm text-gray-600 mb-4">
              Arquivo: <strong>{publishDoc.fileName}</strong>. Opcionalmente sugira uma pasta de destino (o revisor pode alterar).
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <textarea
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-4 text-sm"
              rows={3}
              value={publishNotes}
              onChange={e => setPublishNotes(e.target.value)}
              placeholder="Motivo ou instruções para o revisor..."
            />
            <label className="block text-sm font-medium text-gray-700 mb-1">Pasta sugerida (opcional)</label>
            <input
              type="search"
              className="w-full border border-gray-300 rounded-md px-3 py-2 mb-2 text-sm"
              placeholder="Buscar pasta (mín. 2 caracteres)..."
              value={folderSearch}
              onChange={e => setFolderSearch(e.target.value)}
            />
            {foldersLoading && <p className="text-sm text-gray-500 mb-2">Carregando pastas...</p>}
            {!foldersLoading && folderSearch.trim().length >= 2 && filteredFolders.length === 0 && (
              <p className="text-sm text-gray-500 mb-2">Nenhuma pasta encontrada.</p>
            )}
            <ul className="border border-gray-100 rounded-md max-h-40 overflow-y-auto mb-4 divide-y">
              {filteredFolders.map(f => (
                <li key={f.id}>
                  <button
                    type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                      selectedFolderId === f.id ? 'bg-blue-50 text-blue-800' : ''
                    }`}
                    onClick={() => setSelectedFolderId(f.id === selectedFolderId ? null : f.id)}
                  >
                    <div className="font-medium">{f.title}</div>
                    <div className="text-xs text-gray-500 truncate">{f.fullPath || f.path}</div>
                  </button>
                </li>
              ))}
            </ul>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setPublishDoc(null);
                  setSelectedFolderId(null);
                  setPublishNotes('');
                  setFolderSearch('');
                }}
              >
                Cancelar
              </Button>
              <Button type="button" variant="primary" disabled={publishing} onClick={() => void submitPublish()}>
                {publishing ? 'Enviando...' : 'Enviar solicitação'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!deleteTarget}
        title="Excluir documento privado"
        message={
          deleteTarget
            ? `Remover "${deleteTarget.fileName}" do seu drive? O arquivo e a indexação serão apagados.`
            : ''
        }
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
        confirmText={deleting ? 'Excluindo...' : 'Excluir'}
        cancelText="Cancelar"
      />
    </div>
  );
};

export default MyPrivateDocumentsPage;
